import { useState } from 'preact/hooks';
import { Link } from 'wouter-preact';
import type { Analysis } from '@teacher-shared/analysis';
import { api, type Dataset } from '@/lib/teaching/teacher-api';
import { invalidateDataset, loadDataset, refreshAssessments } from '@/lib/teaching/state';
import { errText } from '@/lib/teaching/slice2';
import { FINALISED_TEXT } from '@/lib/teaching/slice4';
import { Card } from '@/components/teaching/ui';
import { Btn, ErrorLine, inputCls } from '@/components/teaching/form';
import { TeachingPage, ClassSwitcher, useClass, WithData, useAnalysis, AssessmentSwitcher, ProvisionalBanner, breadcrumbOf, studentHref, perfFill, INK, pct, Legend } from '@/components/teaching/ui';

// Guttman matrix: items easiest → hardest, students most → least mastered.
export function Heatmap() {
  return <WithData state={useAnalysis()} title="Mastery grid">{({ a, A, ds }) => <HeatmapView a={a} A={A} ds={ds} />}</WithData>;
}

function HeatmapView({ a, A, ds }: { a: string; A: Analysis; ds: Dataset }) {
  const [cls, setCls] = useClass(A.classes);
  const items = A.items
    .map((item) => ({ item, r: A.itemRate(item, cls) }))
    .sort((a, b) => (b.r.rate ?? 0) - (a.r.rate ?? 0) || a.item.id.localeCompare(b.item.id, undefined, { numeric: true }));
  const rows = A.summariesIn(cls)
    .filter((s) => s.student.status === 'completed')
    .sort((a, b) => b.masteredCount - a.masteredCount || a.student.name.localeCompare(b.student.name));
  const waiting = A.summariesIn(cls).filter((s) => s.student.status !== 'completed');
  const gut = A.guttman(cls);
  const prog = A.progression(cls);

  return (
    <TeachingPage title="Mastery grid" breadcrumb={breadcrumbOf(A)} banner={<ProvisionalBanner A={A} />} actions={<><AssessmentSwitcher current={a} /><ClassSwitcher value={cls} onChange={setCls} classes={A.classes} /></>}>
      <div class="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div class="text-[12.5px] text-[var(--color-text-muted)]" data-guttman={gut.applicable ? 'applicable' : 'na'}>
          {gut.applicable
            ? prog.applicable ? 'Questions run from most to least mastered. Your progression order is below.' : 'Questions are sorted from most to least mastered. Set a teaching order below to see where students get stuck.'
            : <><b class="text-[var(--color-text)]">{gut.reason}.</b> The grid below simply shows who mastered what.</>}
        </div>
        <Legend />
      </div>

      <div class="overflow-x-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-card)]">
        <table class="border-collapse text-[11.5px]">
          <thead>
            <tr>
              <th class="sticky left-0 z-10 bg-[var(--color-card)] text-left px-3 py-2 font-medium text-[var(--color-text-muted)] min-w-[150px]">Student</th>
              {items.map(({ item, r }) => (
                <th key={item.id} class="px-1 py-2 font-medium text-[var(--color-text)] align-bottom min-w-[54px]" title={item.prompt}>
                  <div>{item.id}</div>
                  <div class="text-[9.5px] font-normal text-[var(--color-text-faint)] leading-tight">{item.topic}</div>
                  <div class="text-[9.5px] font-normal text-[var(--color-text-faint)]">{item.strand} · {item.command}</div>
                  <div class="mt-1 rounded px-1 tabular-nums" style={{ background: perfFill(r.rate), color: INK }}>{pct(r.rate)}</div>
                </th>
              ))}
              <th class="px-2 py-2 font-medium text-[var(--color-text-muted)]">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.student.id} class="border-t border-[var(--color-border)]">
                <td class="sticky left-0 z-10 bg-[var(--color-card)] px-3 py-1 whitespace-nowrap">
                  <Link href={studentHref(a, s.student.id, cls)} class="text-[var(--color-text)] hover:text-[var(--color-accent)]">
                    {s.student.name}
                  </Link>
                  {cls === 'all' && <span class="ml-1 text-[10px] text-[var(--color-text-faint)]">{s.student.cls}</span>}
                  {!s.complete && <span class="ml-1 text-[9.5px] px-1 rounded" style={{ background: '#fddcbf', color: INK }}>partial</span>}
                </td>
                {items.map(({ item }) => {
                  const res = s.results.find((x) => x.itemId === item.id);
                  const m = A.mastered(res);
                  const nm = !!res?.notMarked;
                  return (
                    <td key={item.id} class="p-0.5">
                      <div
                        class={'h-7 rounded flex items-center justify-center tabular-nums ' + (m === null && !nm ? 'border border-dashed border-[var(--color-border-strong)]' : '')}
                        style={{ background: nm ? 'var(--color-elevated)' : m === null ? 'transparent' : perfFill(m ? 1 : 0), color: m === null ? 'var(--color-text-faint)' : INK }}
                        title={nm ? `${item.id}: not marked` : m === null ? `${item.id}: ${res?.unavailableReason ?? 'no evidence'}` : `${item.id}: ${res?.mark}/${item.max}`}
                      >
                        {nm ? '–' : m === null ? '' : m ? '1' : '0'}
                      </div>
                    </td>
                  );
                })}
                <td class="px-2 text-center tabular-nums text-[var(--color-text)]">{s.masteredCount}/{s.validCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ProgressionPanel a={a} A={A} ds={ds} prog={prog} />

      {waiting.length > 0 && (
        <div class="mt-3 text-[12px] text-[var(--color-text-faint)]">
          Not in the grid (no evidence yet): {waiting.map((s) => `${s.student.name} (${s.student.status === 'absent' ? 'absent' : 'catch-up pending'})`).join(', ')}
        </div>
      )}
    </TeachingPage>
  );
}

type Prog = ReturnType<Analysis['progression']>;

/** U22: progression claims only with the teacher's order. */
function ProgressionPanel({ a, A, ds, prog }: { a: string; A: Analysis; ds: Dataset; prog: Prog }) {
  const finalised = ds.assessment.status === 'finalised';
  const current = Object.fromEntries((ds.progression ?? []).map((p) => [p.item_id, String(p.step)]));
  const [editing, setEditing] = useState(false);
  const [steps, setSteps] = useState<Record<string, string>>(current);
  const [err, setErr] = useState<string | null>(null);
  async function save(clear = false) {
    setErr(null);
    try {
      const body = clear ? [] : A.items.filter((i) => steps[i.uuid]?.trim()).map((i) => ({ item_id: i.uuid, step: Number(steps[i.uuid]) }));
      await api('PUT', `/api/t/assessments/${a}/progression`, { steps: body, expectedRevision: ds.assessment.revision });
      invalidateDataset(a); await loadDataset(a, true); void refreshAssessments();
      setEditing(false);
    } catch (e) { setErr(errText(e)); }
  }
  const na = (what: string) => <div class="text-[12.5px]"><span class="text-[var(--color-text-faint)] uppercase text-[10.5px] tracking-wider">{what}: </span><span class="text-[var(--color-text-muted)]">{prog.reason}</span></div>;
  return (
    <div class="grid lg:grid-cols-2 gap-3 mt-4">
      <Card title="Where students get stuck" sub="Worked out from your teaching order: the basics, the step most students stall at, and unusual patterns">
        {prog.applicable ? (
          <div class="flex flex-col gap-2 text-[12.5px] text-[var(--color-text)]" data-progression="set">
            <div><span class="text-[var(--color-text-faint)] uppercase text-[10.5px] tracking-wider">The basics: </span>{prog.foundational.join(', ')}</div>
            <div><span class="text-[var(--color-text-faint)] uppercase text-[10.5px] tracking-wider">Sticking point: </span>
              {prog.barrier ? `Step ${prog.barrier.step} (${prog.barrier.items.join(', ')}): ${prog.barrier.failed} of ${prog.barrier.of} students who mastered every lower step did not master it` : 'none found (needs at least 5 students at a step)'}</div>
            <div><span class="text-[var(--color-text-faint)] uppercase text-[10.5px] tracking-wider">Unusual patterns: </span>
              {prog.unusual.length ? prog.unusual.map((u) => `${u.student.name} (mastered step ${u.masteredStep}, missed ${u.failedLower.join(', ')})`).join('; ') : 'none'}</div>
          </div>
        ) : (
          <div class="flex flex-col gap-1" data-progression="na">{na('Sticking point')}{na('The basics')}{na('Unusual patterns')}</div>
        )}
      </Card>
      <Card title="Teaching order" sub="Step 1 = the basics. Questions in the same step are taught together." right={!editing && <Btn disabled={finalised} title={finalised ? FINALISED_TEXT : undefined} onClick={() => { setSteps(current); setEditing(true); }}>{prog.applicable ? 'Edit order' : 'Set order'}</Btn>}>
        {finalised && <div class="text-[12px] text-[var(--color-text-muted)] mb-1">{FINALISED_TEXT}</div>}
        {!editing ? (
          prog.applicable ? <div class="text-[12.5px] text-[var(--color-text)]">{prog.steps.map((s) => `Step ${s.step}: ${s.items.join(', ')}`).join(' · ')}</div>
            : <div class="text-[12.5px] text-[var(--color-text-muted)]">No order set.</div>
        ) : (
          <div class="flex flex-col gap-2">
            <div class="grid grid-cols-[repeat(auto-fill,minmax(92px,1fr))] gap-2">
              {A.items.map((i) => (
                <label key={i.uuid} class="flex items-center gap-1.5 text-[12px] text-[var(--color-text)]">
                  <span class="w-8">{i.id}</span>
                  <input aria-label={`Step for ${i.id}`} type="number" min={1} max={60} class={inputCls + ' w-14'} value={steps[i.uuid] ?? ''}
                    onInput={(e) => setSteps({ ...steps, [i.uuid]: (e.target as HTMLInputElement).value })} />
                </label>
              ))}
            </div>
            <div class="flex flex-wrap gap-2">
              <Btn kind="primary" onClick={() => save()}>Save order</Btn>
              {prog.applicable && <Btn kind="danger" onClick={() => save(true)}>Clear order</Btn>}
              <Btn onClick={() => setEditing(false)}>Cancel</Btn>
            </div>
          </div>
        )}
        <ErrorLine error={err} />
      </Card>
    </div>
  );
}
